import { useEffect, useRef, useState } from 'react';
import { useAlert, useGlobals } from '@/hooks/index';
import { useUserContext } from '@/hooks/index';
import { useDownloadFiles, useEvents } from '@/talons';
import { getOnStorage, removeFromStorage, setOnStorage } from '@/shared';
import { Navigate, useLocation } from 'react-router-dom';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';
import { useDbFunctions } from '@/talons';
import { useNetworkStatusContext } from '@/hooks';
import ModalSync from '@/components/modal-sync/ModalSync';

// 30 minutes
const TIME_TO_CHECK_NEW_DATASETS = 30 * 60 * 1000;
// const TIME_TO_CHECK_NEW_DATASETS = 10 * 1000;

const ProtectedRoute = ({ element, requiredAccess }: any) => {
  const { showAlert } = useAlert();
  const { pathname } = useLocation();
  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const { getLinks } = useDownloadFiles();
  const { isSignedIn } = useUserContext();
  const getOnDB = db.getOnDB;
  const reloadTables = db.reloadTables;
  const { connectivityStatus } = useNetworkStatusContext();
  const { canAccessCheckout, canAccessSuccess, setCanAccessCheckout, forcedReloadTables, setForcedReloadTables } = useGlobals();
  
  const firstLoad = useRef(false);
  const initialSyncStarted = useRef(false);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const [ready, setReady] = useState(false);
  const [showInitialSyncModal, setShowInitialSyncModal] = useState(false);

  useEffect(() => {
    if (requiredAccess !== 'checkout' && canAccessCheckout) {
      setCanAccessCheckout(false);
    }
  }, [requiredAccess, canAccessCheckout, setCanAccessCheckout]);

  useEffect(() => {
    if (!isSignedIn) {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
      return;
    }
    if (timer.current) return;

    timer.current = setInterval(async () => {
      const data = await getLinks({ isBackground: true });

      const isArray = !!data && Array.isArray(data) && data.length > 0;
      const isObject = !!data && !isArray && Object.keys(data).length > 0;

      const hasData = isArray || isObject;
      if (hasData) {
        if (!getOnStorage('has_new_datasets')) {
          showAlert({
            title: 'Detectamos alterações nos dados',
            message: 'Ao navegar para outra página as alterações serão aplicadas.',
            theme: 'warning',
            bordered: true,
            borderLeft: true,
            showIcon: true,
            close: 'icon',
          });
        }
        setOnStorage('has_new_datasets', true);
      }
    }, TIME_TO_CHECK_NEW_DATASETS);

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [isSignedIn]);

  useEffect(() => {
    const fetchTables = async () => {
      await reloadTables();
    }
    if (!forcedReloadTables) {
      fetchTables();
      setForcedReloadTables(true);
    }
  }, []);

  // Before rendering protected content, ensure essential data exists. If missing and online, open ModalSync to fetch.
  useEffect(() => {
    const run = async () => {
      if (!isSignedIn) return;
      if (ready) return;
      // If offline, don't block
      if (connectivityStatus === 'offline') {
        setReady(true);
        return;
      }

      const essentialTables = ['cliente', 'produtos', 'categoria'] as const;
      const results = await Promise.all(
        essentialTables.map(async (t) => {
          const data = await getOnDB(t as any);
          if (Array.isArray(data)) return data.length > 0;
          if (data && typeof data === 'object') return Object.keys(data).length > 0;
          return false;
        })
      );

      const datasets = (getOnStorage('datasets') || []) as Array<{ TIPO_ARQUIVO: string }>;

      let missingFromDatasets = false;
      if (Array.isArray(datasets) && datasets.length > 0) {
        const datasetChecks = await Promise.all(
          datasets.map(async (d) => {
            const data = await getOnDB(d.TIPO_ARQUIVO as any);
            if (Array.isArray(data)) return data.length > 0;
            if (data && typeof data === 'object') return Object.keys(data).length > 0;
            return false;
          })
        );
        missingFromDatasets = datasetChecks.some((ok) => !ok);
      }

      const needSync = datasets.length === 0 || results.some((r) => !r) || missingFromDatasets;

      if (needSync && !initialSyncStarted.current) {
        initialSyncStarted.current = true;
        setShowInitialSyncModal(true);
        return;
      }

      // No sync needed
      setReady(true);
    };

    run();
  }, [isSignedIn, connectivityStatus, getOnDB, getLinks, ready]);

  useEffect(() => {
    if (getOnStorage('has_new_datasets')) {
      removeFromStorage('has_new_datasets');
      unregisterServiceWorker({ preserveLocalStorage: true });
    }
    if (getOnStorage('has_new_datasets')) {
      window.location.reload();
    }
  }, [pathname, isSignedIn]);

  useEffect(() => {
    const run = async () => {
      if (!firstLoad.current) {
        firstLoad.current = true;
        return;
      }
      await registerEvent({
        tipoEvento: 'NETWORK_STATUS',
        obsEvento: `Status da conexão: ${connectivityStatus}`,
      });
    };
    run();
  }, [connectivityStatus]);

  if (!isSignedIn) {
    return <Navigate to="/login" />;
  }

  // If a first-sync is required, show the modal loader and delay route rendering
  if (showInitialSyncModal) {
    return (
      <>
        <ModalSync
          isLoading
          cbAction={() => {
            setReady(true);
            setShowInitialSyncModal(false);
          }}
        />
      </>
    );
  }

  // Hold route rendering until initial data check completes
  if (!ready) return null;

  if (requiredAccess === 'checkout' && !canAccessCheckout) {
    return <Navigate to="/carrinho" />;
  }

  if (requiredAccess === 'success' && !canAccessSuccess) {
    return <Navigate to="/checkout" />;
  }

  return element;
};

export default ProtectedRoute;
